import React, {useState, useEffect, useCallback, useRef} from "react"
import {Box, Typography, Chip, Button, Skeleton, Collapse, Link, CircularProgress, Tooltip, Avatar} from "@mui/material"
import {alpha} from "@mui/material/styles"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import PersonIcon from "@mui/icons-material/Person"
import SupportAgentIcon from "@mui/icons-material/SupportAgent"
import SmartToyIcon from "@mui/icons-material/SmartToy"
import {format} from "date-fns"

import {DraftReplyPanel} from "./DraftReplyPanel"

import {api} from "@indebted/api"
import type {ConversationMessage, ConversationMessageAttachment, ConversationTimeline} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

const PAGE_SIZE = 5

// A sent reply becomes readable only once the provider's webhook has written
// the sent email, so the wait is a round trip nobody here controls and it has
// run to about a minute. Look often while it might already be there, then settle
// into the last interval and keep looking: there is no length of wait that would
// make giving up the right answer, since the reply is sent either way.
const REPLY_SYNC_INTERVALS_MS = [700, 700, 1000, 1500, 2500, 4000, 6000, 8000, 10000, 15000]
const REPLY_SYNC_FAILURES_BEFORE_REPORTING = 3
const REPLY_BODY_GRACE_MS = 10 * 60 * 1000

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

function formatOccurredAt(occurredAt: string): string {
	return format(new Date(occurredAt), "d MMM yyyy, HH:mm")
}

function formatAttachmentSize(sizeBytes: number): string {
	if (sizeBytes < 1024) return `${sizeBytes} B`
	if (sizeBytes < 1024 * 1024) return `${Math.round(sizeBytes / 1024)} KB`
	return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
}

// Each direction is a voice in the conversation: who speaks, the face beside
// the message, and whether the card carries the outbound indigo tint.
function senderProps(direction: string): {
	name: string
	avatar: React.ReactElement
	kindChip?: string
	outbound: boolean
} {
	const avatarSx = {width: 32, height: 32}
	const automatedSender = (kindChip: string) => ({
		name: "InDebted",
		avatar: (
			<Avatar sx={(theme) => ({...avatarSx, bgcolor: alpha(theme.palette.primary.main, 0.12)})}>
				<SmartToyIcon fontSize="small" color="primary" />
			</Avatar>
		),
		kindChip,
		outbound: true,
	})
	switch (direction) {
		case "inbound":
			return {
				name: "Customer",
				avatar: (
					<Avatar sx={{...avatarSx, bgcolor: "secondary.main"}}>
						<PersonIcon fontSize="small" />
					</Avatar>
				),
				outbound: false,
			}
		case "agent-reply":
			return {
				name: "InDebted",
				avatar: (
					<Avatar sx={{...avatarSx, bgcolor: "primary.main"}}>
						<SupportAgentIcon fontSize="small" />
					</Avatar>
				),
				outbound: true,
			}
		case "information-request":
			return automatedSender("Information request")
		case "acknowledgement":
			return automatedSender("Acknowledgement")
		case "case-verification-link":
			return automatedSender("Verification link")
		default:
			return automatedSender("Auto-reply")
	}
}

function deliveryChipProps(status: string): {label: string; color: "success" | "warning" | "error"} | null {
	switch (status) {
		case "delivered":
			return {label: "Delivered", color: "success"}
		case "temporarily-undelivered":
			return {label: "Delivery delayed", color: "warning"}
		case "permanently-undelivered":
			return {label: "Not delivered", color: "error"}
		default:
			return null
	}
}

// A reply row reuses the providerMessageId of the inbound message it answers, so
// the timeline can hold two rows sharing that id; identity has to include the
// direction (and occurredAt) to stay unique for React keys, refs, and highlight.
function rowKey(message: ConversationMessage): string {
	return `${message.direction}:${message.providerMessageId}:${message.occurredAt}`
}

// Screen-reader-only styling for the live region that announces a sent reply's
// progress into the timeline.
const srOnly = {
	position: "absolute",
	width: "1px",
	height: "1px",
	margin: "-1px",
	padding: 0,
	overflow: "hidden",
	clip: "rect(0 0 0 0)",
	whiteSpace: "nowrap",
	border: 0,
} as const

// Held only until the send's own row shows up, which is why it counts replies
// rather than naming one: a reply whose source inbound carried no Message-ID
// header is projected with no inReplyTo, and matching on that would leave this
// waiting for a row that had already arrived.
type PendingReply = {
	sentAt: string
	repliesBefore: number
}

function agentReplyCount(messages: ConversationMessage[]): number {
	return messages.filter((m) => m.direction === "agent-reply").length
}

// A reply's row is projected from the send, but its body is read from the sent
// email, which the pipeline writes afterwards, so the row is served bodiless in
// between. That row is the server's own account of the gap, which is what makes
// it survive the agent closing the conversation and opening it again.
//
// Only while it is recent. A reply whose email was never written stays bodiless
// for good, and holding its place forever would be a promise nothing can keep,
// on a spinner that outlives the agent's shift. Past the grace it is shown as
// the row it is.
function isUnreadableReply(message: ConversationMessage): boolean {
	return (
		message.direction === "agent-reply" &&
		message.body === "" &&
		Date.now() - new Date(message.occurredAt).getTime() < REPLY_BODY_GRACE_MS
	)
}

function hasUnreadableReply(messages: ConversationMessage[]): boolean {
	return messages.some(isUnreadableReply)
}

// A sent reply is not readable until the provider's webhook has written the
// sent email, so this holds its place in the thread meanwhile. It shows that
// the reply is coming, never the reply itself: the text lives in the browser
// and the thread is the server's account, and the one time this row rendered
// that text it read as the message being deleted when the server's own row
// arrived. Two things it must not do: say "sending", because the reply has
// already left and an agent who reads it as unsent sends it twice, and claim
// the timeline lacks it, because nothing would revisit that claim.
function PendingReplyRow({sentAt}: {sentAt: string}) {
	const sender = senderProps("agent-reply")
	const row = useRef<HTMLLIElement>(null)

	// The thread is newest first, so the row appears above an agent who is
	// reading the message they just answered.
	useEffect(() => {
		row.current?.scrollIntoView({
			behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
			block: "nearest",
		})
	}, [])

	return (
		<Box component="li" ref={row} display="flex" gap={1.5} sx={{listStyle: "none"}}>
			{sender.avatar}
			<Box
				flex={1}
				minWidth={0}
				p={2.5}
				border="1px solid"
				borderColor="transparent"
				borderRadius={2}
				sx={(theme) => ({bgcolor: alpha(theme.palette.primary.main, 0.04)})}
			>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={0.25}>
					<Typography variant="subtitle2" color="textPrimary">
						{sender.name}
					</Typography>
					<Typography variant="caption" color="textSecondary" whiteSpace="nowrap" mt={0.25}>
						{formatOccurredAt(sentAt)}
					</Typography>
				</Box>
				<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
					<Chip label="Sent" size="small" color="success" variant="outlined" />
					<Box display="flex" alignItems="center" gap={0.75}>
						<CircularProgress size={12} aria-hidden />
						<Typography variant="caption" color="textSecondary">
							Adding to the timeline…
						</Typography>
					</Box>
				</Box>
				<Box mt={1.5} maxWidth="80ch">
					<Skeleton variant="text" width="92%" />
					<Skeleton variant="text" width="86%" />
					<Skeleton variant="text" width="45%" />
				</Box>
			</Box>
		</Box>
	)
}

type ReplyAction =
	| {kind: "draft"; onDraftReply: () => void}
	| {kind: "pick-up"; onPickUp: () => void; pickingUp: boolean}
	| {kind: "held-by-another-agent"}

function ReplySlot({action, isReplyTarget}: {action: ReplyAction; isReplyTarget: boolean}) {
	if (action.kind === "pick-up") {
		return (
			<Tooltip title="Pick up this case to reply">
				<span tabIndex={action.pickingUp ? 0 : undefined}>
					<Button
						variant="outlined"
						size="small"
						onClick={action.onPickUp}
						disabled={action.pickingUp}
						sx={{alignSelf: "flex-start"}}
					>
						PICK UP CASE
					</Button>
				</span>
			</Tooltip>
		)
	}

	if (action.kind === "held-by-another-agent") {
		return (
			<Tooltip title="This conversation's case is assigned to another agent">
				<span tabIndex={0}>
					<Button variant="outlined" size="small" disabled sx={{alignSelf: "flex-start"}}>
						Draft reply
					</Button>
				</span>
			</Tooltip>
		)
	}

	if (isReplyTarget) {
		return (
			<Typography role="status" variant="caption" color="primary" fontWeight={500}>
				Replying here
			</Typography>
		)
	}

	return (
		<Button variant="outlined" size="small" onClick={action.onDraftReply} sx={{alignSelf: "flex-start"}}>
			Draft reply
		</Button>
	)
}

function MessageRow({
	message,
	customerID,
	conversationID,
	threadSubject,
	isAnchor,
	isReplyTarget,
	targetIsEarlier,
	isHighlighted,
	replyToTarget,
	onJumpToTarget,
	replyAction,
	registerRef,
}: {
	message: ConversationMessage
	customerID: string
	conversationID: string
	threadSubject: string
	isAnchor: boolean
	isReplyTarget: boolean
	targetIsEarlier: boolean
	isHighlighted: boolean
	replyToTarget?: {rowKey: string; occurredAt: string}
	onJumpToTarget?: (rowKey: string) => void
	replyAction?: ReplyAction
	registerRef?: (element: HTMLElement | null) => void
}) {
	const [showQuoted, setShowQuoted] = useState(false)
	const {notification} = useNotification()
	const isInbound = message.direction === "inbound"
	const sender = senderProps(message.direction)
	const deliveryChip = message.deliveryStatus ? deliveryChipProps(message.deliveryStatus) : null
	const headerChip =
		isReplyTarget && targetIsEarlier ? (
			<Chip label="Replying to this" size="small" color="primary" />
		) : isAnchor ? (
			targetIsEarlier ? (
				<Chip label="Latest message" size="small" />
			) : (
				<Chip label="Reply anchor" size="small" color="primary" />
			)
		) : null

	const hasMetaRow =
		Boolean(headerChip) ||
		Boolean(message.direction === "agent-reply" && message.authoredBy) ||
		Boolean(isInbound && message.classificationCategory) ||
		Boolean(message.direction === "agent-reply" && message.edited) ||
		Boolean(deliveryChip)

	const downloadAttachment = async (attachment: ConversationMessageAttachment) => {
		try {
			// API Gateway only base64-decodes the response when the request asks for a
			// binary media type, so a plain navigation (text/html Accept) would save the
			// raw base64 text instead of the file.
			const response = await fetch(
				api.customer.getConversationMessageAttachmentURL(
					customerID,
					conversationID,
					message.providerMessageId,
					attachment.index,
				),
				{headers: {Accept: "application/octet-stream"}},
			)
			if (!response.ok) {
				notification.error("Failed to download the attachment.")
				return
			}
			const objectURL = URL.createObjectURL(await response.blob())
			const link = document.createElement("a")
			link.href = objectURL
			link.download = attachment.filename
			document.body.appendChild(link)
			link.click()
			link.remove()
			URL.revokeObjectURL(objectURL)
		} catch {
			notification.error("Failed to download the attachment.")
		}
	}

	return (
		<Box component="li" ref={registerRef} display="flex" gap={1.5} sx={{listStyle: "none"}}>
			{sender.avatar}
			<Box
				flex={1}
				minWidth={0}
				p={2.5}
				border="1px solid"
				borderColor={isReplyTarget ? "primary.main" : sender.outbound ? "transparent" : "grey.200"}
				borderRadius={2}
				sx={(theme) => ({
					transition: "background-color 300ms ease",
					bgcolor: isHighlighted
						? "action.selected"
						: sender.outbound
							? alpha(theme.palette.primary.main, 0.04)
							: "background.paper",
				})}
			>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={0.25}>
					<Box display="flex" alignItems="center" gap={1} minWidth={0} flexWrap="wrap">
						<Typography variant="subtitle2" color="textPrimary">
							{sender.name}
						</Typography>
						{sender.kindChip && <Chip label={sender.kindChip} size="small" variant="outlined" />}
					</Box>
					<Typography variant="caption" color="textSecondary" whiteSpace="nowrap" mt={0.25}>
						{formatOccurredAt(message.occurredAt)}
					</Typography>
				</Box>
				{message.subject && message.subject !== threadSubject && (
					<Typography variant="caption" color="textSecondary" display="block">
						Subject: <Box component="span">{message.subject}</Box>
					</Typography>
				)}
				{hasMetaRow && (
					<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
						{headerChip}
						{message.direction === "agent-reply" && message.authoredBy && (
							<Typography variant="caption" color="textSecondary">
								by {message.authoredBy}
							</Typography>
						)}
						{isInbound && message.classificationCategory && (
							<Chip label={message.classificationCategory} size="small" variant="outlined" />
						)}
						{message.direction === "agent-reply" && message.edited && (
							<Chip label="Edited" size="small" variant="outlined" />
						)}
						{deliveryChip && (
							<>
								<Chip
									label={deliveryChip.label}
									size="small"
									color={deliveryChip.color}
									variant="outlined"
								/>
								{message.deliveryStatus === "delivered" && message.deliveryOccurredAt && (
									<Typography variant="caption" color="textSecondary">
										at {formatOccurredAt(message.deliveryOccurredAt)}
									</Typography>
								)}
								{message.deliveryStatus !== "delivered" && message.deliveryReason && (
									<Typography variant="caption" color="textSecondary">
										· {message.deliveryReason}
									</Typography>
								)}
							</>
						)}
					</Box>
				)}
				{message.inReplyToProviderMessageId && (
					<Box mt={0.5}>
						{replyToTarget && onJumpToTarget ? (
							<Link
								component="button"
								type="button"
								variant="caption"
								underline="hover"
								onClick={() => onJumpToTarget(replyToTarget.rowKey)}
							>
								↳ In reply to the message from {formatOccurredAt(replyToTarget.occurredAt)}
							</Link>
						) : (
							<Typography variant="caption" color="textSecondary">
								↳ In reply to an earlier message
							</Typography>
						)}
					</Box>
				)}
				{message.body && (
					<Typography
						variant="body2"
						color="textPrimary"
						mt={1.5}
						sx={{whiteSpace: "pre-wrap", maxWidth: "80ch", lineHeight: 1.65}}
					>
						{message.body}
					</Typography>
				)}
				{message.attachments && message.attachments.length > 0 && (
					<Box mt={1} display="flex" flexWrap="wrap" gap={1}>
						{message.attachments.map((attachment) =>
							isInbound ? (
								<Chip
									key={attachment.index}
									label={attachment.filename}
									title={attachment.filename}
									size="small"
									icon={<AttachFileIcon />}
									onClick={() => downloadAttachment(attachment)}
								/>
							) : (
								<Box key={attachment.index} display="flex" alignItems="center" gap={0.5}>
									<Chip
										label={attachment.filename}
										title={attachment.filename}
										size="small"
										icon={<AttachFileIcon />}
									/>
									<Typography variant="caption" color="textSecondary">
										{formatAttachmentSize(attachment.sizeBytes)}
									</Typography>
								</Box>
							),
						)}
					</Box>
				)}
				{message.quoted && (
					<Box mt={1}>
						<Button
							variant="text"
							size="small"
							onClick={() => setShowQuoted((shown) => !shown)}
							sx={{alignSelf: "flex-start"}}
						>
							{showQuoted ? "Hide quoted text" : "Show quoted text"}
						</Button>
						<Collapse in={showQuoted} unmountOnExit>
							<Typography
								variant="body2"
								color="textSecondary"
								sx={{
									whiteSpace: "pre-wrap",
									borderLeft: "2px solid",
									borderColor: "grey.200",
									pl: 1.5,
									mt: 0.5,
								}}
							>
								{message.quoted}
							</Typography>
						</Collapse>
					</Box>
				)}
				{replyAction && (
					<Box mt={1}>
						<ReplySlot action={replyAction} isReplyTarget={isReplyTarget} />
					</Box>
				)}
			</Box>
		</Box>
	)
}

function ConversationDetail({
	customerID,
	conversationID,
	customerEmails,
	preferredCaseID,
	onPickedUp,
}: {
	customerID: string
	conversationID: string
	customerEmails?: string[]
	preferredCaseID?: string
	onPickedUp?: () => void
}) {
	const {notification} = useNotification()
	const [agentEmail, agentName] = useAgent()
	const [timeline, setTimeline] = useState<ConversationTimeline | null>(null)
	const [messages, setMessages] = useState<ConversationMessage[]>([])
	const [hasMore, setHasMore] = useState(false)
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [selectedReplyMessageId, setSelectedReplyMessageId] = useState<string | null>(null)
	const [highlightedRowKey, setHighlightedRowKey] = useState<string | null>(null)
	const [pendingReply, setPendingReply] = useState<PendingReply | null>(null)
	const [pickingUpCase, setPickingUpCase] = useState(false)

	const mounted = useRef(true)
	const messageRefs = useRef<Record<string, HTMLElement | null>>({})
	const highlightTimer = useRef<ReturnType<typeof setTimeout>>()
	const loadedCountRef = useRef(0)
	const syncGeneration = useRef(0)
	useEffect(() => {
		return () => {
			mounted.current = false
			clearTimeout(highlightTimer.current)
		}
	}, [])

	const jumpToMessage = useCallback((key: string) => {
		const element = messageRefs.current[key]
		if (!element) return
		element.scrollIntoView({behavior: "smooth", block: "center"})
		setHighlightedRowKey(key)
		clearTimeout(highlightTimer.current)
		highlightTimer.current = setTimeout(() => {
			if (mounted.current) setHighlightedRowKey(null)
		}, 1600)
	}, [])

	const fetchTimeline = useCallback(async () => {
		// Refetch covers every message already loaded, so it keeps the "Show earlier"
		// history instead of collapsing back to the first page.
		const conversation = await api.customer.getConversation(customerID, conversationID, {
			limit: Math.max(PAGE_SIZE, loadedCountRef.current),
			offset: 0,
			agentEmail,
		})
		if (!mounted.current) return null
		setTimeline(conversation)
		setMessages(conversation.messages)
		setHasMore(conversation.hasMore)
		// A poll issued before "Show earlier" must not answer with a shorter page
		// and shrink the thread back under the agent.
		loadedCountRef.current = Math.max(loadedCountRef.current, conversation.messages.length)
		return conversation
	}, [customerID, conversationID, agentEmail])

	const reloadConversation = useCallback(async () => {
		try {
			await fetchTimeline()
		} catch {
			if (mounted.current) notification.error("Failed to load the conversation")
		}
	}, [fetchTimeline, notification])

	const waitForReadableReplies = useCallback(
		async (expectedReplies: number) => {
			// The reply is recorded synchronously but its timeline row is projected
			// asynchronously, and the body arrives later still, so keep looking until
			// the send's row is there and every reply in the thread can be read.
			// A newer send supersedes this poll, so it must not report the older
			// reply's outcome against the newer reply's row.
			const generation = ++syncGeneration.current
			const superseded = () => !mounted.current || syncGeneration.current !== generation
			let consecutiveFailures = 0
			let interval = REPLY_SYNC_INTERVALS_MS[0]

			for (let attempt = 0; ; attempt++) {
				if (superseded()) return
				try {
					const conversation = await fetchTimeline()
					if (!conversation || superseded()) return
					if (
						agentReplyCount(conversation.messages) >= expectedReplies &&
						!hasUnreadableReply(conversation.messages)
					) {
						return
					}
					consecutiveFailures = 0
				} catch {
					if (superseded()) return
					// One failed read is a blip and the next one usually answers. A run
					// of them is the reason the row is not moving, and saying nothing
					// leaves the agent watching a placeholder that will never resolve.
					consecutiveFailures++
					if (consecutiveFailures === REPLY_SYNC_FAILURES_BEFORE_REPORTING) {
						notification.error("Failed to load the conversation")
					}
				}
				await delay(interval)
				// The schedule stretches rather than running out, and holding the last
				// interval rather than indexing past it means no arithmetic can hand
				// delay an undefined and turn the wait into an unthrottled read loop.
				interval = REPLY_SYNC_INTERVALS_MS[attempt + 1] ?? interval
			}
		},
		[fetchTimeline, notification],
	)

	useEffect(() => {
		if (!agentEmail) return
		setLoading(true)
		reloadConversation().finally(() => {
			if (mounted.current) setLoading(false)
		})
	}, [reloadConversation, agentEmail])

	// A conversation can be opened while a reply is still unreadable, by the
	// agent who sent it coming back or by anyone else. Whoever is looking, the
	// row resolves rather than waiting for them to reload the page.
	useEffect(() => {
		if (loading || !hasUnreadableReply(messages)) return
		void waitForReadableReplies(agentReplyCount(messages))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading])

	async function pickUpLinkedCase(caseID: string, email: string, name: string) {
		setPickingUpCase(true)
		try {
			await api.customer.pickUpCase(customerID, caseID, {AgentEmail: email, AgentName: name})
			notification.success("Case picked up")
		} catch (error: any) {
			notification.error(error?.Message ?? "Failed to pick up case")
		}
		// Two agents can race for the same case, so both outcomes settle against the server.
		await reloadConversation()
		onPickedUp?.()
		if (mounted.current) setPickingUpCase(false)
	}

	function showEarlier() {
		setLoadingMore(true)
		api.customer
			.getConversation(customerID, conversationID, {limit: PAGE_SIZE, offset: messages.length})
			.then((conversation) => {
				setMessages((current) => [...current, ...conversation.messages])
				setHasMore(conversation.hasMore)
				loadedCountRef.current += conversation.messages.length
			})
			.catch(() => {
				notification.error("Failed to load earlier messages")
			})
			.finally(() => {
				setLoadingMore(false)
			})
	}

	if (loading) {
		return (
			<Box role="status" aria-label="Loading conversation" display="flex" flexDirection="column" gap={2}>
				{[0, 1].map((i) => (
					<Box key={i} display="flex" gap={1.5}>
						<Skeleton variant="circular" width={32} height={32} />
						<Box flex={1} p={2.5} border="1px solid" borderColor="grey.200" borderRadius={2}>
							<Box display="flex" justifyContent="space-between" mb={1.5}>
								<Skeleton variant="text" width={160} />
								<Skeleton variant="text" width={90} />
							</Box>
							<Skeleton variant="text" width="90%" />
							<Skeleton variant="text" width="75%" />
						</Box>
					</Box>
				))}
			</Box>
		)
	}

	if (!timeline) {
		return null
	}

	const replyTarget = selectedReplyMessageId ?? timeline.replyAnchorProviderMessageId
	const targetIsEarlier = Boolean(replyTarget) && replyTarget !== timeline.replyAnchorProviderMessageId
	const inboundById = new Map(messages.filter((m) => m.direction === "inbound").map((m) => [m.providerMessageId, m]))
	// The send's row has not shown up yet, so nothing on the server stands for it.
	const awaitingRow = pendingReply !== null && agentReplyCount(messages) <= pendingReply.repliesBefore
	const awaitingProjection = awaitingRow || hasUnreadableReply(messages)
	// A screen reader user hears this rather than seeing it. It says what the
	// draft panel's own confirmation does not, and it says when the wait ends,
	// because emptying a live region announces nothing.
	const pendingReplyAnnouncement = awaitingProjection
		? "Your reply will appear in the conversation shortly."
		: pendingReply
			? "Your reply is now in the conversation."
			: ""
	// Messages render newest first, so the newest subject titles the thread.
	const threadSubject = messages.length > 0 ? messages[0].subject : ""

	// A backend that predates case ownership answers without these fields, and a
	// conversation that cannot be read is worse than one that cannot be gated.
	const linkedCases = timeline.linkedCases ?? []
	const ownershipGated = timeline.caseOwnershipEnabled && linkedCases.length > 0
	const ownsLinkedCase = linkedCases.some((linked) => !!linked.assigneeEmail && linked.assigneeEmail === agentEmail)
	const unassignedLinkedCase =
		linkedCases.find((linked) => linked.caseId === preferredCaseID && !linked.assigneeEmail) ??
		linkedCases.find((linked) => !linked.assigneeEmail)
	const canDraftReply = !ownershipGated || ownsLinkedCase

	const replyActionFor = (message: ConversationMessage): ReplyAction | undefined => {
		if (message.direction !== "inbound") return undefined
		if (canDraftReply) {
			return {kind: "draft", onDraftReply: () => setSelectedReplyMessageId(message.providerMessageId)}
		}
		if (!unassignedLinkedCase) return {kind: "held-by-another-agent"}
		if (!agentEmail || !agentName) return undefined
		return {
			kind: "pick-up",
			pickingUp: pickingUpCase,
			onPickUp: () => void pickUpLinkedCase(unassignedLinkedCase.caseId, agentEmail, agentName),
		}
	}

	return (
		<Box
			sx={{borderTop: "1px solid", borderColor: "grey.200", pt: 2.5}}
			display="flex"
			flexDirection="column"
			gap={2}
		>
			{threadSubject && (
				<Typography variant="subtitle1" component="h3" fontWeight={600} color="textPrimary">
					{threadSubject}
				</Typography>
			)}
			{/* Mounted from the start and empty until there is something to say. A
			live region created with its text already inside goes unannounced. */}
			<Box aria-live="polite" sx={srOnly}>
				{pendingReplyAnnouncement}
			</Box>
			<Box display="flex" gap={3} alignItems="flex-start">
				<Box flex={1} display="flex" flexDirection="column" gap={1.5} minWidth={0}>
					<Box component="ul" display="flex" flexDirection="column" gap={2} m={0} p={0}>
						{pendingReply && awaitingRow && <PendingReplyRow sentAt={pendingReply.sentAt} />}
						{messages.map((m) => {
							// The server says a reply is there but cannot yet say what it
							// is, so its place is held rather than drawn as an empty card.
							if (isUnreadableReply(m)) {
								return <PendingReplyRow key={rowKey(m)} sentAt={m.occurredAt} />
							}
							const answeredMessage = m.inReplyToProviderMessageId
								? inboundById.get(m.inReplyToProviderMessageId)
								: undefined
							const key = rowKey(m)
							const isInbound = m.direction === "inbound"
							return (
								<MessageRow
									key={key}
									message={m}
									customerID={customerID}
									conversationID={conversationID}
									threadSubject={threadSubject}
									isAnchor={
										isInbound && m.providerMessageId === timeline.replyAnchorProviderMessageId
									}
									isReplyTarget={isInbound && m.providerMessageId === replyTarget}
									targetIsEarlier={targetIsEarlier}
									isHighlighted={highlightedRowKey === key}
									replyToTarget={
										answeredMessage
											? {rowKey: rowKey(answeredMessage), occurredAt: answeredMessage.occurredAt}
											: undefined
									}
									onJumpToTarget={jumpToMessage}
									replyAction={replyActionFor(m)}
									registerRef={(element) => {
										messageRefs.current[key] = element
									}}
								/>
							)
						})}
						{hasMore && (
							<Button
								variant="text"
								size="small"
								onClick={showEarlier}
								disabled={loadingMore}
								sx={{alignSelf: "flex-start"}}
							>
								{loadingMore ? "Loading…" : "Show earlier"}
							</Button>
						)}
					</Box>
				</Box>
				{replyTarget && canDraftReply && (
					<Box flex={1} minWidth={0} maxWidth={960}>
						<DraftReplyPanel
							key={replyTarget}
							customerID={customerID}
							conversationID={conversationID}
							providerMessageId={replyTarget}
							customerEmails={customerEmails}
							repliesToEarlierMessage={targetIsEarlier}
							autoGenerate={ownershipGated && ownsLinkedCase}
							onSent={() => {
								// Pin the panel to the message just answered, or an inbound
								// arriving during the sync would move the anchor, remount the
								// panel, and replace the confirmation with an unasked draft.
								setSelectedReplyMessageId(replyTarget)
								const repliesBefore = agentReplyCount(messages)
								setPendingReply({sentAt: new Date().toISOString(), repliesBefore})
								void waitForReadableReplies(repliesBefore + 1)
							}}
						/>
					</Box>
				)}
			</Box>
		</Box>
	)
}

export {ConversationDetail}
